import type { ReactNode } from 'react'
import type { SessionSummary, Show } from '@/lib/server/shows'

import { useMemo } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { Sheet } from '@/components/generals/sheet'
import { Media } from '@/components/generals/media'
import { FadeLink } from '@/components/generals/fade-link'
import { dateKey, formatKey, formatTime } from '@/lib/time'

interface BookingPanelProps {
    show: Show | null
    sessions: SessionSummary[]
    onClose: () => void
}

/** Choosing a show opens this panel: its next performances, each one a step from choosing seats. */
export const BookingPanel = ({ show, sessions, onClose }: BookingPanelProps): ReactNode => {
    const grouped = useMemo(() => {
        const groups: { key: string, items: SessionSummary[] }[] = []

        for (const session of sessions.filter(item => item.showSlug === show?.slug).slice(0, 14)) {
            const key = dateKey(new Date(session.startsAt))
            const group = groups.find(item => item.key === key)

            if (group) group.items.push(session)
            else groups.push({ key, items: [session] })
        }

        return groups
    }, [sessions, show])

    return (
        <Sheet
            open={show !== null}
            onClose={onClose}
            eyebrow={show ? `${show.kind} · ${show.duration} minutes · ${show.ageLabel}` : undefined}
            title={show?.title ?? ''}
            footer={(
                <FadeLink href='/shows' className='label-mono inline-flex items-center gap-3 text-star'>
                    <span className='h-px w-8 bg-brass' />
                    Every date and time
                </FadeLink>
            )}
        >
            {show && (
                <div>
                    <div className='grid grid-cols-[6rem_1fr] gap-5'>
                        <Media src={show.poster ?? '/images/planetarium-theatre.jpg'} alt={show.posterAlt} sizes='96px' className='aspect-[3/4] w-24' />
                        <p className='text-[0.95rem] leading-relaxed text-star-2'>
                            {show.description}
                        </p>
                    </div>

                    <p className='label-mono mt-10 text-star-3'>
                        Choose a performance
                    </p>

                    {grouped.length === 0 && (
                        <p className='mt-4 text-star-2'>
                            No performances are on sale in the next two weeks. See every date on the shows page.
                        </p>
                    )}

                    <div className='mt-4 space-y-6'>
                        {grouped.map(group => (
                            <div key={group.key}>
                                <p className='font-heading text-xl text-star'>
                                    {formatKey(group.key, 'long')}
                                </p>
                                <ul className='mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3'>
                                    {group.items.map(session => (
                                        <li key={session.id}>
                                            {session.available > 0 ? (
                                                <FadeLink
                                                    href={`/shows/book/${session.id}`}
                                                    className='group flex items-center justify-between gap-2 border border-hair px-3 py-3 transition-colors duration-500 hover:border-brass'
                                                >
                                                    <span>
                                                        <span className='data-mono block text-star'>
                                                            {formatTime(new Date(session.startsAt))}
                                                        </span>
                                                        <span className='label-mono block text-[0.55rem] text-star-3'>
                                                            {session.available < 20 ? `${session.available} left` : 'Choose seats'}
                                                        </span>
                                                    </span>
                                                    <ArrowUpRight aria-hidden className='size-3.5 text-brass' strokeWidth={1.25} />
                                                </FadeLink>
                                            ) : (
                                                <span className='block border border-hair px-3 py-3 opacity-50'>
                                                    <span className='data-mono block text-star-2'>
                                                        {formatTime(new Date(session.startsAt))}
                                                    </span>
                                                    <span className='label-mono block text-[0.55rem] text-star-3'>
                                                        Sold out
                                                    </span>
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {show.posterCredit && (
                        <p className='mt-10 text-xs text-star-3'>
                            {`Poster image: ${show.posterCredit}`}
                        </p>
                    )}
                </div>
            )}
        </Sheet>
    )
}
